import { useSyncExternalStore } from 'react';
import { locales, mosaicCopy, persistLocale, type Locale } from './i18n.js';

type Theme = 'dark' | 'light';

const themeStorageKey = 'a-vernalis-theme';
const cookieLifetime = 60 * 60 * 24 * 365;
const themeColors: Record<Theme, string> = {
  dark: '#171b17',
  light: '#e7e0d1'
};

function subscribeToTheme(onStoreChange: () => void) {
  window.addEventListener('a-vernalis-themechange', onStoreChange);
  window.addEventListener('storage', onStoreChange);
  return () => {
    window.removeEventListener('a-vernalis-themechange', onStoreChange);
    window.removeEventListener('storage', onStoreChange);
  };
}

function getThemeSnapshot(): Theme {
  return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
}

function getServerThemeSnapshot(): Theme {
  return 'dark';
}

function ThemeToggle({ locale }: { locale: Locale }) {
  const theme = useSyncExternalStore(subscribeToTheme, getThemeSnapshot, getServerThemeSnapshot);

  const toggleTheme = () => {
    const nextTheme: Theme = getThemeSnapshot() === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset.theme = nextTheme;
    document.documentElement.style.colorScheme = nextTheme;
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', themeColors[nextTheme]);
    try {
      localStorage.setItem(themeStorageKey, nextTheme);
    } catch {
      // Storage can be disabled by the browser.
    }

    const sharedDomain = location.hostname.endsWith('a-vernalis.com')
      ? '; Domain=.a-vernalis.com'
      : '';
    try {
      document.cookie = `${themeStorageKey}=${nextTheme}; Path=/; Max-Age=${cookieLifetime}; SameSite=Lax; Secure${sharedDomain}`;
    } catch {
      // Cookie access can be disabled by the browser.
    }
    window.dispatchEvent(new Event('a-vernalis-themechange'));
  };

  const label =
    theme === 'dark' ? mosaicCopy[locale].switchToLight : mosaicCopy[locale].switchToDark;

  return (
    <button
      className="theme-toggle"
      type="button"
      onClick={toggleTheme}
      aria-label={label}
      title={label}
    >
      <span className="theme-toggle-option theme-toggle-sun" aria-hidden="true">
        ☼
      </span>
      <span className="theme-toggle-option theme-toggle-moon" aria-hidden="true">
        ☾
      </span>
      <span className="theme-toggle-thumb" aria-hidden="true" />
    </button>
  );
}

export function SiteControls({
  locale,
  onLocaleChange
}: {
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
}) {
  const selectLocale = (nextLocale: Locale) => {
    persistLocale(nextLocale);
    onLocaleChange(nextLocale);
  };

  return (
    <>
      <nav className="language-switcher" aria-label={mosaicCopy[locale].chooseLanguage}>
        {locales.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={option === locale}
            onClick={() => selectLocale(option)}
          >
            {option.toUpperCase()}
          </button>
        ))}
      </nav>
      <ThemeToggle locale={locale} />
    </>
  );
}

export function MosaicMasthead({ locale, connection }: { locale: Locale; connection: string }) {
  const copy = mosaicCopy[locale];
  const connectionLabel =
    copy.connection[connection as keyof typeof copy.connection] ?? connection.toUpperCase();

  return (
    <header className="rest-masthead">
      <a
        className="route-back"
        href={`https://index.a-vernalis.com/${locale}/rest/miscellanea`}
        aria-label={`${copy.back}: ${copy.miscellanea}`}
      >
        <i className="route-back-arrow" aria-hidden="true" />
        <span className="route-back-copy">
          <small>{copy.back}</small>
          <strong>{copy.miscellanea}</strong>
        </span>
      </a>
      <p>
        <span lang="grc">ΨΗΦΙΔΩΤΟΝ</span> · {connectionLabel}
      </p>
    </header>
  );
}
